import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Flame, Skull, Loader, X, Shield } from "lucide-react";

/**
 * Daily login streak claim modal.
 *
 * Polls /api/streak/status on auth-mount. If claimable, opens an inline
 * banner (not blocking) with a "CLAIM" button. Claim awards the horde
 * bonus, marks today, and the modal closes.
 *
 * Reward curve: day 1 = +2, day 2 = +3, day 3 = +5, day 4 = +7,
 *               day 5 = +10, day 6 = +14, day 7+ = +20 (cap).
 */
export default function StreakBanner() {
  const { auth, refreshState } = useAuth();
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [justClaimed, setJustClaimed] = useState(null);

  const load = useCallback(async () => {
    if (!auth) return;
    try {
      const { data } = await api.get("/streak/status");
      setData(data);
    } catch (e) {
      // non-blocking
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const claim = async () => {
    setActing(true);
    try {
      const { data: res } = await api.post("/streak/claim");
      setJustClaimed(res);
      await refreshState();
    } catch (e) {
      // ignore
    } finally {
      setActing(false);
    }
  };

  if (!auth || dismissed) return null;

  // Just-claimed celebration
  if (justClaimed && justClaimed.ok) {
    return (
      <div className="narrative-panel relative fade-up" data-testid="streak-claimed">
        <button onClick={() => setDismissed(true)}
                className="absolute top-3 right-3 text-muted-h hover:text-blood transition-colors"
                aria-label="Dismiss"
                data-testid="streak-dismiss">
          <X className="w-3 h-3" />
        </button>
        <div className="flex items-baseline gap-3 mb-2">
          {justClaimed.shield_used
            ? <Shield className="w-4 h-4 text-blood" strokeWidth={1.5} />
            : <Flame className="w-4 h-4 text-blood" strokeWidth={1.5} />}
          <p className="tac-label">
            {justClaimed.shield_used
              ? `SHIELD HELD // STREAK SAVED`
              : `DAY ${justClaimed.streak} // STREAK CONTINUES`}
          </p>
        </div>
        <p className="text-sm text-bone leading-relaxed">
          {justClaimed.shield_used
            ? `A shield absorbed your absence. Streak unbroken at ${justClaimed.streak}.`
            : <>+{justClaimed.reward} horde for the dead. {justClaimed.transition === "fresh" || justClaimed.transition === "reset"
                ? "A new line in the count begins."
                : `${justClaimed.streak} days unbroken.`}</>}
        </p>
        {justClaimed.shield_earned && (
          <p className="text-[0.7rem] text-blood mt-2 tracking-wider flex items-center gap-1.5"
             data-testid="streak-shield-earned">
            <Shield className="w-3 h-3" /> SHIELD EARNED · {justClaimed.shields} OF 3
          </p>
        )}
        {justClaimed.next_reward && !justClaimed.shield_used && (
          <p className="text-[0.65rem] text-muted-h mt-2 tracking-wider">
            TOMORROW · +{justClaimed.next_reward} HORDE
          </p>
        )}
      </div>
    );
  }

  if (!data || !data.claimable) {
    // Show shield count + sparkline even when nothing is claimable
    if (data && (data.shields > 0 || data.history?.length > 0) && !dismissed) {
      return (
        <div className="tactical-card" data-testid="streak-shields-only">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Shield className="w-3 h-3 text-blood" strokeWidth={1.5} />
              <p className="text-[0.7rem] font-mono tracking-widest text-muted-h">
                STREAK · DAY {data.streak}
                {data.shields > 0 && <> · <span className="text-bone">{data.shields} SHIELD{data.shields !== 1 ? "S" : ""}</span> READY</>}
              </p>
            </div>
            <Sparkline history={data.history} />
          </div>
        </div>
      );
    }
    return null;
  }

  const isReset = (data.streak ?? 0) === 1;

  return (
    <div className={`narrative-panel relative ${
      data.endangered ? "streak-endangered" : ""
    }`} data-testid="streak-banner">
      <button onClick={() => setDismissed(true)}
              className="absolute top-3 right-3 text-muted-h hover:text-blood transition-colors"
              aria-label="Dismiss"
              data-testid="streak-dismiss">
        <X className="w-3 h-3" />
      </button>
      <div className="flex items-baseline gap-3 mb-2">
        <Flame className={`w-4 h-4 text-blood ${data.endangered ? "animate-pulse" : ""}`}
               strokeWidth={1.5} />
        <p className="tac-label" data-testid="streak-label">
          {data.endangered
            ? `STREAK ENDS IN ${Math.max(1, Math.round(data.hours_until_reset))}H`
            : isReset
              ? "RETURN OF THE DEAD"
              : `DAY ${data.streak} // STREAK ALIVE`}
        </p>
      </div>
      <p className="text-sm text-[var(--text-main)] leading-relaxed mb-4">
        {data.endangered ? (
          <>
            The dirt is impatient. Claim before midnight UTC or the streak dies with the day.
          </>
        ) : isReset ? (
          "You return after silence. Begin again."
        ) : (
          <>
            Your column has waited {data.streak - 1 === 0 ? "another day" : data.streak + " days"} in the dark.{" "}Claim today's tribute.
          </>
        )}
      </p>
      {data.history && data.history.length > 0 && (
        <div className="mb-4" data-testid="streak-sparkline-wrap">
          <Sparkline history={data.history} />
        </div>
      )}
      <button onClick={claim}
              disabled={acting}
              className="btn-blood w-full"
              data-testid="streak-claim-button">
        {acting ? <Loader className="w-3 h-3 animate-spin" /> : <Skull className="w-3 h-3" />}
        CLAIM // +{data.reward_preview} HORDE
      </button>
      {data.shields > 0 && (
        <p className="text-[0.65rem] text-muted-h mt-3 tracking-widest flex items-center gap-1.5"
           data-testid="streak-shields-count">
          <Shield className="w-3 h-3 text-blood" />
          {data.shields} SHIELD{data.shields !== 1 ? "S" : ""} READY · ABSORBS A MISSED DAY
        </p>
      )}
    </div>
  );
}


// 7-day sparkline. Solid blood dot = claimed, hollow grey ring = missed,
// crossed shield-icon = saved_by_shield, dotted ring = today (future).
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const Sparkline = ({ history }) => {
  if (!history || history.length === 0) return null;
  return (
    <div className="flex items-end gap-1.5" data-testid="streak-sparkline">
      {history.map((d, i) => {
        const dt = new Date(d.date + "T00:00:00Z");
        const dow = dt.getUTCDay();
        const cls =
          d.status === "claimed"     ? "bg-blood"
        : d.status === "saved_by_shield" ? "bg-bone/70"
        : d.status === "future"      ? "border border-dashed border-blood bg-transparent"
        :                              "border border-[var(--border-subtle)] bg-transparent";
        return (
          <div key={d.date + i} className="flex flex-col items-center gap-1"
               title={`${d.date} — ${d.status}`}
               data-testid={`streak-day-${i}-${d.status}`}>
            <div className={`w-2.5 h-2.5 rounded-full ${cls}`} />
            <span className="text-[0.55rem] font-mono text-muted-h">{DAY_LABELS[dow]}</span>
          </div>
        );
      })}
    </div>
  );
};
